import { PHONE_MIN_DIGITS } from '@/constants/phone-codes'
import { CONTACT_FORM_ERROR_KEYS } from '@/composables/contact/contact-form.rules'
import { findCountryByCode } from '@/composables/phone/detect/phone-code-auto-detect.helpers'

// Ключі для i18n
const PHONE_ERROR_KEYS = {
  format: 'index.contactForm.errors.phoneFormat',
  minLength: 'index.contactForm.errors.phoneMinLength',
  maxLength: 'index.contactForm.errors.phoneMaxLength',
  length: 'index.contactForm.errors.phoneLength',
  mustStartWithPlus: 'index.contactForm.errors.phoneMustStartWithPlus',
  onlyDigits: 'index.contactForm.errors.phoneOnlyDigits',
  oneSpace: 'index.contactForm.errors.phoneOneSpace',
  spaceAfterCode: 'index.contactForm.errors.phoneSpaceAfterCode',
} as const

/** Витягує лише цифри з номера (UA380634050363 -> 380634050363) */
function getDigits(value: string): string {
  return (value ?? '').replace(/\D/g, '')
}

/**
 * Правило валідації: обов'язковість поля телефону
 * Приймає формат UA380634050363 або 380634050363
 */
export const phoneRequiredRule = (value: string): boolean | string => {
  if (!value?.trim()) {
    return CONTACT_FORM_ERROR_KEYS.required
  }
  const digits = getDigits(value)
  return digits.length >= PHONE_MIN_DIGITS ? true : CONTACT_FORM_ERROR_KEYS.required
}

/**
 * Правило валідації: мінімальна довжина телефону.
 * Для країн з totalLength — мінімум = totalLength (фіксована довжина перевіряється в phoneLengthRule).
 * Для країн з minLength (діапазон) — номер після коду не коротший за minLength.
 * Інакше — не коротше 7 цифр після коду.
 */
export const phoneMinLengthRule = (value: string): boolean | string => {
  if (!value?.trim()) return true
  const digits = getDigits(value)
  if (digits.length < 2) return true
  const country = findCountryByCode(digits)
  if (!country) return true
  const numberWithoutCode = digits.substring(country.phoneCode.length)
  const minRequired = country.totalLength != null
    ? country.totalLength - country.phoneCode.length
    : (country.minLength ?? 7)
  return numberWithoutCode.length >= minRequired ? true : PHONE_ERROR_KEYS.minLength
}

/**
 * Правило валідації: точна довжина телефону.
 * Для країн з totalLength — загальна довжина цифр має дорівнювати totalLength.
 * Для країн з minLength === maxLength (діапазон з одного значення) — довжина після коду = minLength.
 */
export const phoneLengthRule = (value: string): boolean | string => {
  if (!value?.trim()) return true
  const digits = getDigits(value)
  if (digits.length < 2) return true
  const country = findCountryByCode(digits)
  if (!country) return true
  if (country.totalLength != null) {
    return digits.length === country.totalLength ? true : PHONE_ERROR_KEYS.length
  }
  const minLen = country.minLength
  const maxLen = country.maxLength
  if (minLen == null || maxLen == null || minLen !== maxLen) return true
  const numberWithoutCode = digits.substring(country.phoneCode.length)
  return numberWithoutCode.length === minLen ? true : PHONE_ERROR_KEYS.length
}

/**
 * Правило валідації: максимальна довжина телефону.
 * Якщо в країни задано totalLength — загальна довжина цифр не повинна перевищувати totalLength.
 * Якщо задано maxLength (діапазон, без totalLength) — довжина номера після коду не більша за maxLength.
 * Інакше — не більше 15 цифр загалом.
 */
export const phoneMaxLengthRule = (value: string): boolean | string => {
  if (!value?.trim()) return true
  const digits = getDigits(value)
  if (digits.length < 2) return true
  const country = findCountryByCode(digits)
  if (!country) return digits.length > 15 ? PHONE_ERROR_KEYS.maxLength : true
  if (country.totalLength != null) {
    return digits.length <= country.totalLength ? true : PHONE_ERROR_KEYS.maxLength
  }
  if (country.maxLength != null) {
    const numberWithoutCode = digits.substring(country.phoneCode.length)
    return numberWithoutCode.length <= country.maxLength ? true : PHONE_ERROR_KEYS.maxLength
  }
  return digits.length > 15 ? PHONE_ERROR_KEYS.maxLength : true
}

/**
 * Правило валідації: формат телефону
 * Приймає UA380634050363 (ISO+digits) або 380634050363 (тільки цифри)
 */
export const phoneFormatRule = (value: string): boolean | string => {
  if (!value?.trim()) return true
  const trimmed = value.trim()
  if (/^[A-Z]{2}\d+$/i.test(trimmed)) return true
  if (/^\d+$/.test(trimmed)) return true
  return PHONE_ERROR_KEYS.format
}

